/**
 * Store: Discount campaigns
 * -----------------------------------------------------------------------------
 * Active and promoted campaign states.
 * - Tied to the discount code application component.
 * - Also used to trigger query of discount code validity.
 * - Only JSDOC custom parameters, ignore module state or object.
 *
 */
import { defineStore } from 'pinia';
import { decodeApiId, transformId } from '~/helpers/graphql';
import { checkFreebies } from '~/helpers/freebies';
import { checkIncludedOrExcludedProducts } from '~/helpers/discounts';
import variantIdQuery from '~graphql/nodes-by-id-product-inline.gql';
import { formatProduct } from '~/format/format-product';
import useCartStore from './cart';

const useDiscountCampaignsStore = defineStore('discount-campaigns', {
  state: () => ({
    active: false,
    code: '',
    urlDiscount: false,
    validNativeCode: '',
    codeState: 'ready',
    cartItemPrices: [{}],
    errorType: '',
    promoted: false,
    thresholds: [],
    oldThresholds: [],
    freeProducts: [],
  }),

  getters: {
    getXforYProducts: (state) => {
      return (logic, type) => {
        if (
          !state[type] ||
          !logic ||
          (!logic.discountPreview && type === 'promoted') ||
          logic.onSale
        ) {
          return 0;
        }

        const XforYDiscounts = state[type].discounts.filter(
          (entry) => entry.calculationType === 'buy-x-get-y',
        );

        if (!XforYDiscounts) {
          return false;
        }

        const discounts = XforYDiscounts.filter((entry) => {
          return checkIncludedOrExcludedProducts(entry, logic);
        });

        return discounts?.length ? discounts[0] : false;
      };
    },

    /**
     * Get best campaign discount for collection/product/variant ID.
     * - Update wishlist custom product cards logic too.
     * @param {Object} logic - Product and variant data.
     * @param {Boolean} logic.discountPreview - Show campaign preview.
     * @param {Number} logic.collectionIds - Collection IDs.
     * @param {Boolean} logic.onSale - Product has sale price.
     * @param {Number} logic.productId - Product ID.
     * @param {Number} logic.variantId - Variant ID.
     * @param {String} type - Type of campaign - `active` or `promoted`.
     * @returns {Number}
     */
    getCampaignDiscount: (state) => {
      return (logic, type) => {
        if (
          !state[type] ||
          !logic ||
          (!logic.discountPreview && type === 'promoted') ||
          logic.onSale
        ) {
          return 0;
        }

        const discounts = state[type].discounts
          .filter((entry) => {
            return checkIncludedOrExcludedProducts(entry, logic);
          })
          .filter(Boolean)
          .map((entry) => entry.discount)
          .sort((a, b) => b - a);

        return discounts?.length ? discounts[0] : 0;
      };
    },

    /**
     * Get campaign thresholds for free shipping and freebies.
     * - Also determines if threshold is active.
     * @param {String} type - Type of campaign - `active` or `promoted`.
     * @returns {Array<Object>}
     */
    getCampaignThresholds: (state) => {
      return (type) => {
        const cartStore = useCartStore();
        const cartTotal = cartStore.getIncludedCartTotal;
        const cartItems = cartStore.getIncludedCartItems;

        let freeProducts = [];
        let freebieQuantity = 0;

        /**
         * Early return empty list if no campaign is active.
         */
        if (!state[type]?.freeProducts?.length) {
          return [];
        }

        const productBased = checkFreebies(
          state[type]?.freeProducts,
          'product',
          cartItems,
          state.code.toLowerCase(),
        );

        const thresholdBased = checkFreebies(
          state[type]?.freeProducts,
          'threshold',
          cartItems,
          state.code.toLowerCase(),
        );

        if (productBased.length > 0) {
          freeProducts = freeProducts.concat(productBased);
        }

        if (thresholdBased.length > 0) {
          freeProducts = freeProducts.concat(thresholdBased);
        }

        freebieQuantity = freeProducts.length;

        /**
         * Calculate free thresholds and whether they're active.
         */
        const freebieThresholds = freeProducts
          .map((freebie) => {
            let activeState, autoAdd, freebieType, remaining, threshold;
            /**
             * We need to adjust the value according to the freebie type
             */
            if (freebie.type === 'product') {
              activeState = true;
              autoAdd = Boolean(freebie.freeProductsAutoInsertActive);
              remaining = 0;
              threshold = 0;
              freebieType = 'product';
              freebieQuantity = 1;
            } else {
              threshold = freebie.thresholds.find((thresholdObject) => {
                return thresholdObject.marketHandle === cnvs.store.marketName;
              });

              if (!threshold) {
                return false;
              }

              threshold = threshold.threshold * 100;
              activeState = cartTotal >= threshold;
              autoAdd = Boolean(freebie.freeProductsAutoInsertActive);
              freebieQuantity = freebie.quantity;
              remaining = threshold - cartTotal;
              freebieType = 'threshold';
            }

            /**
             * get the currently active code from storage as state is not
             * updating after clear code properly
             */
            const discountCampaigns = JSON.parse(
              localStorage.getItem('discount-campaigns'),
            );
            const code = discountCampaigns?.code.toLowerCase();
            let handle = `${state[type].campaignId}-${freebieType}-freebie-all-${threshold}`;
            /** we need the code if there is a threshold that is influencer specific */
            if (
              freebie.freeProductsInfluencerOnlyActive &&
              freebieType !== 'product'
            ) {
              handle = `${state[type].campaignId}-${freebieType}-freebie-${threshold}-${code}}`;
            }

            /**
             * If the freebie is product-based, we need to add the condition IDs
             */
            if (freebieType === 'product') {
              handle = `${state[type].campaignId}-${freebieType}-freebie-${freebie.groupId}-${code}-${freebie.cartItemId}}`;
              /**
               * if the freebie is not influencer based, it's free for all so we don't want an
               * INFL at the start or a code connected to it
               */
              if (!freebie.freeProductsInfluencerOnlyActive) {
                const campaignId = state[type].campaignId.replace('INFL_', '');
                handle = `${campaignId}-${freebieType}-freebie-${freebie.groupId}-all-${freebie.cartItemId}}`;
              }
            }

            return {
              active: activeState,
              autoAdd,
              handle,
              quantity: freebieQuantity,
              freeProducts: [],
              remaining,
              threshold,
              type: freebieType,
              variantIds: freebie.variantIds,
            };
          })
          .filter(Boolean);

        /**
         * Combine and sort by threshold ascending.
         */
        const thresholds = freebieThresholds.sort(
          (a, b) => a.threshold - b.threshold,
        );
        return thresholds;
      };
    },

    /**
     * Go through already saved cart item prices and return current discounted price.
     * @param {Number} variantId - Variant ID.
     * @param {Number} price - Current price.
     * @returns {Number}
     */
    getCurrentItemDiscountedPrice: (state) => {
      return (variantId, price) => {
        const newItem = state.cartItemPrices.find(
          (item) => item.variant_id === variantId,
        );
        return newItem ? newItem.discounted_price : price;
      };
    },
  },

  actions: {
    /**
     * Commit campaign.
     * @param {Object} data - Data object to set.
     * @param {Object} campaign - Campaign to set.
     * @param {String} type - Type of campaign - `active` or `promoted`.
     */
    setCampaign({ campaign, type }) {
      this[type] = campaign;
      if (type === 'active' && !campaign) {
        this.thresholds = [];
      }
    },

    refreshCampaignThresholds() {
      this.oldThresholds = this.thresholds;

      if (this.active) {
        const newThresholds = this.getCampaignThresholds('active');

        this.thresholds = newThresholds.map((threshold) => {
          if (threshold.variantIds) {
            const freeProducts = this.freeProducts.filter((product) => {
              return threshold.variantIds.includes(
                product.selectedOrFirstAvailableVariant.id.toString(),
              );
            });

            return { ...threshold, freeProducts };
          }

          return threshold;
        });
      } else {
        this.thresholds = [];
      }
    },

    /**
     * Commit discount code query.
     * @param {String} code - Discount code to query.
     */
    setCode(code) {
      this.code = code;
    },

    /**
     * Commit discount code query.
     * @param {String} code - Discount code to query.
     */
    setInitialCode(initialCode) {
      if (initialCode !== this.code) {
        this.code = initialCode;
        this.urlDiscount = true;
      }
    },

    /**
     * Commit discount code query state.
     * @param {String} codeState - Discount code query state.
     */
    setCodeState(codeState) {
      this.codeState = codeState;
    },

    /**
     * Commit thresholds for the active campaign.
     * @param {Object} thresholds - Campaign thresholds
     */
    setCampaignThresholds(thresholds) {
      this.thresholds = thresholds;
    },
    /**
     * Set cart item prices state.
     * @param {Object} CartItemPrices - Prices for the items in the cart.
     */
    setCartItemPrices(cartItemPrices) {
      this.cartItemPrices = cartItemPrices;
    },

    /**
     * Commit discount code error type.
     * @param {String} errorType - Discount code error type.
     */
    setErrorType(errorType) {
      if (!errorType) {
        this.errorType = '';
        return;
      }

      this.errorType = errorType;
    },

    /**
     * Commit flag if discount derives from url apply.
     * This is set to true if the discount code from cart has changed on initial load.
     * @param {Boolean} urlDiscount - Flag if discount derives from url apply.
     */
    setUrlDiscount(urlDiscount) {
      this.urlDiscount = urlDiscount;
    },

    /**
     * Verifies if discount code is applicable to cart.
     * @returns {Promise}
     */
    verifyDiscountCodeIsApplicable(code) {
      return new Promise(async (resolve, reject) => {
        try {
          const cartStore = useCartStore();
          const discount = code === '' ? this.code : code?.toLowerCase();

          if (discount === '') {
            resolve();
            return;
          }

          const payload = {
            discount,
          };

          const response = await cartStore.updateCartAttributes(payload);
          const isApplicable = response.discount_codes[0]?.applicable;

          await this.setCartItemPrices(response.items);
          this.validNativeCode = isApplicable ? code : '';

          resolve(response);
        } catch (error) {
          cnvs.ReportError('Failed to set cart attributes', error);
          reject(error);
        }
      });
    },

    async loadFreeProducts() {
      let variantQueryIds = [];
      this.thresholds.forEach((threshold) => {
        if (threshold.variantIds) {
          variantQueryIds = variantQueryIds.concat(
            ...threshold.variantIds.map((id) =>
              transformId(id, 'ProductVariant'),
            ),
          );
        }
      });
      variantQueryIds = [...new Set(variantQueryIds)];

      const response = await cnvs.Query({
        query: variantIdQuery,
        variables: {
          country: cnvs.store.country,
          ids: variantQueryIds,
          language: cnvs.store.language,
          limit: cnvs.discount.freebies.limit,
        },
      });

      const formattedProducts = response.nodes
        .map((node) => {
          if (!node) {
            return false;
          }

          const variantId = decodeApiId(node.id);
          return { ...formatProduct(node.product, { variantId }) };
        })
        .filter(Boolean);

      this.freeProducts = formattedProducts;

      const newThresholds = this.thresholds.map((threshold) => {
        if (threshold.variantIds) {
          const freeProducts = formattedProducts.filter((product) => {
            return threshold.variantIds.includes(
              product.selectedOrFirstAvailableVariant.id.toString(),
            );
          });

          return { ...threshold, freeProducts };
        }

        return threshold;
      });

      this.thresholds = newThresholds;
    },
  },
  persist: true,
});

export default useDiscountCampaignsStore;
